import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Music, Music2 } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

const COPY = {
  en: {
    eyebrow: "Voices of Our Clients",
    title: "Watch real owners share their story.",
    subtitle:
      "These videos were recorded by our own clients — unscripted, honest, and from the heart.",
    music: "Ambient music",
    musicOff: "Music off",
    musicOn: "Music on",
    tapPlay: "Tap to play",
    tapUnmute: "Tap to unmute",
    items: [
      { name: "Mohammed Khambati", role: "Head of Operations · M. Khambati & Co" },
      { name: "CEO & Founder", role: "Real Estate & Trading Group · Identity confidential" },
      { name: "CEO & Founder", role: "7+ Warehouse Management Products · Identity confidential" },
    ],
  },
  hi: {
    eyebrow: "हमारे ग्राहकों की आवाज़",
    title: "असली मालिकों की कहानी देखिए।",
    subtitle:
      "ये वीडियो हमारे ग्राहकों ने ख़ुद रिकॉर्ड किए — बिना स्क्रिप्ट के, दिल से।",
    music: "हल्का संगीत",
    musicOff: "संगीत बंद",
    musicOn: "संगीत चालू",
    tapPlay: "चलाने के लिए टैप करें",
    tapUnmute: "अनम्यूट के लिए टैप करें",
    items: [
      { name: "मोहम्मद खंबाती", role: "Head of Operations · M. Khambati & Co" },
      { name: "CEO और फ़ाउंडर", role: "रियल एस्टेट और ट्रेडिंग ग्रुप · पहचान गोपनीय" },
      { name: "CEO और फ़ाउंडर", role: "7+ वेयरहाउस मैनेजमेंट प्रोडक्ट्स · पहचान गोपनीय" },
    ],
  },
};

const SOURCES = [
  { src: "/videos/testimonial-1-720p.mp4", poster: "/videos/poster-1.jpg" },
  { src: "/videos/testimonial-2-720p.mp4", poster: "/videos/poster-2.jpg" },
  { src: "/videos/testimonial-3-720p.mp4", poster: "/videos/poster-3.jpg" },
];

const blockEvent = (e) => {
  e.preventDefault();
  return false;
};

function Watermark({ phase }) {
  // 4 positions cycled — moving watermark deters re-recording crops
  const positions = [
    { top: "8%", left: "8%" },
    { top: "8%", right: "8%" },
    { bottom: "12%", right: "8%" },
    { bottom: "12%", left: "8%" },
  ];
  const pos = positions[phase % positions.length];
  return (
    <div
      className="absolute pointer-events-none transition-all duration-[3000ms] ease-in-out z-20"
      style={pos}
      aria-hidden="true"
    >
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/35 backdrop-blur-sm border border-white/15 select-none">
        <img
          src="/logo-icon-white.png"
          alt=""
          className="h-4 w-auto opacity-80"
          draggable="false"
        />
        <span className="text-[10px] font-semibold text-white/85 tracking-wider uppercase">
          launchmitra.in
        </span>
      </div>
    </div>
  );
}

function ProtectedVideo({ src, poster, onAudibleChange, isAudible, label, copy }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [phase, setPhase] = useState(0);

  // Animate watermark position every 3s while playing
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setPhase((p) => p + 1), 3000);
    return () => clearInterval(t);
  }, [playing]);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
        setPlaying(true);
      } catch (_) {}
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    onAudibleChange?.(!next);
  };

  // External request: when bg music turns on, mute videos
  useEffect(() => {
    if (isAudible === false) {
      const v = videoRef.current;
      if (v) {
        v.muted = true;
        setMuted(true);
      }
    }
  }, [isAudible]);

  return (
    <div
      className="group relative rounded-3xl overflow-hidden border border-white/10 bg-slate-900 aspect-[9/16] select-none"
      onContextMenu={blockEvent}
      data-testid={`video-card-${label}`}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        playsInline
        muted={muted}
        preload="metadata"
        controls={false}
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        disablePictureInPicture
        disableRemotePlayback
        onContextMenu={blockEvent}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ WebkitUserSelect: "none", userSelect: "none" }}
      />

      {/* Anti-screenshot/recording overlay (catches drag-save attempts) */}
      <div
        className="absolute inset-0 z-10"
        onContextMenu={blockEvent}
        onDragStart={blockEvent}
        onClick={togglePlay}
        role="button"
        aria-label={playing ? "Pause" : "Play"}
      />

      {/* Floating watermark */}
      <Watermark phase={phase} />

      {/* Bottom gradient + label */}
      <div className="absolute inset-x-0 bottom-0 z-10 p-4 md:p-5 bg-gradient-to-t from-black/85 via-black/50 to-transparent pointer-events-none">
        <div className="font-display text-base md:text-lg font-bold text-white leading-tight">
          {label.name}
        </div>
        <div className="text-[11px] md:text-xs text-slate-300">{label.role}</div>
      </div>

      {/* Top control bar */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={toggleMute}
          className="w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur border border-white/15 text-white flex items-center justify-center transition"
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid={`video-mute-${label.idx}`}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Big center play button when paused */}
      {!playing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-white/20 blur-xl" />
            <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 text-slate-950 flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
              <Play className="w-7 h-7 md:w-8 md:h-8 ml-1" fill="currentColor" />
            </div>
          </div>
          <div className="absolute bottom-20 inset-x-0 text-center text-white/90 text-[11px] font-semibold tracking-wide pointer-events-none">
            {copy.tapPlay}
          </div>
        </div>
      )}

      {/* Mute hint when playing & muted */}
      {playing && muted && (
        <div className="absolute top-3 left-3 z-20 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur border border-white/15 text-[10px] font-semibold text-white tracking-wider uppercase pointer-events-none">
          {copy.tapUnmute}
        </div>
      )}
    </div>
  );
}

export default function VideoTestimonials() {
  const { lang } = useLang();
  const c = COPY[lang];
  const audioRef = useRef(null);
  const [musicOn, setMusicOn] = useState(false);
  const [audibleVideo, setAudibleVideo] = useState(false);

  const toggleMusic = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        a.volume = 0.35;
        await a.play();
        setMusicOn(true);
        setAudibleVideo(false); // mute videos when music starts
      } catch (_) {}
    } else {
      a.pause();
      setMusicOn(false);
    }
  };

  // Pause music if any video is unmuted (audibleVideo=true)
  useEffect(() => {
    const a = audioRef.current;
    if (audibleVideo && a && !a.paused) {
      a.pause();
      setMusicOn(false);
    }
  }, [audibleVideo]);

  return (
    <section
      className="relative py-20 md:py-28 bg-[#0B1020] text-white overflow-hidden"
      data-testid="video-testimonials-section"
      onContextMenu={blockEvent}
    >
      <div className="absolute inset-0 grain opacity-25 pointer-events-none" />
      <div className="absolute -top-40 left-1/4 w-[40rem] h-[40rem] rounded-full bg-blue-600/15 blur-3xl" />
      <div className="absolute -bottom-40 right-1/4 w-[40rem] h-[40rem] rounded-full bg-violet-600/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
              {c.eyebrow}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
              {c.title}
            </h2>
            <p className="text-slate-300 mt-4 text-base md:text-lg">{c.subtitle}</p>
          </div>

          <button
            onClick={toggleMusic}
            data-testid="bg-music-toggle"
            className={`inline-flex items-center gap-2 h-11 px-5 rounded-full border text-sm font-semibold transition ${
              musicOn
                ? "bg-white text-slate-950 border-white"
                : "bg-white/5 hover:bg-white/10 text-white border-white/20"
            }`}
            aria-pressed={musicOn}
          >
            {musicOn ? <Music2 className="w-4 h-4" /> : <Music className="w-4 h-4" />}
            <span>{musicOn ? c.musicOn : c.musicOff}</span>
          </button>
          {/* Background music — local file, low volume */}
          <audio
            ref={audioRef}
            src="/videos/ambient-bg.m4a"
            loop
            preload="none"
            controlsList="nodownload"
            onContextMenu={blockEvent}
          />
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {SOURCES.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.08, duration: 0.55 }}
            >
              <ProtectedVideo
                src={s.src}
                poster={s.poster}
                label={{ ...c.items[i], idx: i }}
                copy={c}
                isAudible={audibleVideo}
                onAudibleChange={(v) => setAudibleVideo(v)}
              />
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-center text-[11px] text-slate-500 max-w-xl mx-auto">
          © Launch Mitra. These testimonial videos are protected and not for redistribution.
        </p>
      </div>
    </section>
  );
}
